'use client';

import { useState } from 'react';
import { useAISettings } from '../contexts/AISettingsContext';
import { ProviderSelector } from './ai-settings/ProviderSelector';
import { ModelConfigurator } from './ai-settings/ModelConfigurator';
import { APIKeyManager } from './ai-settings/APIKeyManager';
import { AdvancedSettings } from './ai-settings/AdvancedSettings';
import { MCPConfiguration } from './ai-settings/MCPConfiguration';
import { SettingsImportExport } from './ai-settings/SettingsImportExport';
import { motion, AnimatePresence } from 'framer-motion';

export function AISettingsPanel() {
  const {
    settings,
    providers,
    hasUnsavedChanges,
    updateSettings,
    saveSettings,
    resetSettings,
    switchProvider,
    switchModel,
    updateApiKey,
    removeApiKey,
    testConnection,
    exportSettings,
    importSettings,
  } = useAISettings();

  const [activeSection, setActiveSection] = useState<'general' | 'advanced' | 'mcp'>('general');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveSettings();
    } finally {
      setIsSaving(false);
    }
  };

  const currentProvider = providers.find(p => p.id === settings.provider);

  return (
    <div className="h-full bg-[#0C0C0D] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-xl">🤖</span>
              AI Settings
            </h2>
            <p className="text-sm text-white/70">
              Configure AI providers, models, and API keys
            </p>
          </div>
          {hasUnsavedChanges && (
            <div className="flex gap-2">
              <button
                onClick={resetSettings}
                className="px-3 py-1 text-xs text-white/60 hover:text-white/80 border border-white/20 rounded transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-3 py-1 text-xs bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-500/50 text-white rounded transition-colors flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-white/10">
        {[
          { id: 'general', label: 'General', icon: '⚙️' },
          { id: 'advanced', label: 'Advanced', icon: '🔧' },
          { id: 'mcp', label: 'MCP', icon: '🔌' },
        ].map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id as any)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all ${
              activeSection === section.id
                ? 'text-white bg-white/5 border-b-2 border-indigo-500'
                : 'text-white/60 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            <span className="text-xs">{section.icon}</span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <AnimatePresence mode="wait">
          {activeSection === 'general' && (
            <motion.div
              key="general"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6 space-y-6"
            >
              {/* Provider Selection */}
              <ProviderSelector
                providers={providers}
                selectedProvider={settings.provider}
                onProviderChange={switchProvider}
                onTestConnection={testConnection}
              />

              {/* Model Configuration */}
              {currentProvider && (
                <ModelConfigurator
                  provider={currentProvider}
                  selectedModel={settings.model}
                  onModelChange={switchModel}
                />
              )}

              {/* API Key Management */}
              <APIKeyManager
                providers={providers}
                apiKeys={settings.apiKeys}
                onUpdateKey={updateApiKey}
                onRemoveKey={removeApiKey}
              />

              {/* Import/Export */}
              <SettingsImportExport
                onExport={exportSettings}
                onImport={importSettings}
              />
            </motion.div>
          )}

          {activeSection === 'advanced' && (
            <motion.div
              key="advanced"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6"
            >
              <AdvancedSettings
                settings={settings.advanced}
                onChange={(advanced) => updateSettings({ advanced })}
              />
            </motion.div>
          )}

          {activeSection === 'mcp' && (
            <motion.div
              key="mcp"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6"
            >
              <MCPConfiguration
                settings={settings.mcp}
                onChange={(mcp) => updateSettings({ mcp })}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Status */}
      <div className="p-4 border-t border-white/10 bg-white/5">
        <div className="flex items-center justify-between text-xs text-white/60">
          <div className="flex items-center gap-4">
            <span>Provider: {currentProvider?.name || 'None'}</span>
            <span>Model: {settings.model || 'None'}</span>
          </div>
          <div className="flex items-center gap-2">
            {currentProvider?.status === 'connected' && (
              <span className="flex items-center gap-1 text-green-400">
                <span className="w-2 h-2 bg-green-400 rounded-full" />
                Connected
              </span>
            )}
            {currentProvider?.status === 'error' && (
              <span className="flex items-center gap-1 text-red-400">
                <span className="w-2 h-2 bg-red-400 rounded-full" />
                Error
              </span>
            )}
            {currentProvider?.status === 'checking' && (
              <span className="flex items-center gap-1 text-yellow-400">
                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                Checking...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}